import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { GridRenderCellParams, GridRowId, useGridApiContext } from '@mui/x-data-grid';
import { DesktopDatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import i18n from 'i18next';
import moment from 'moment';
import React, { ChangeEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IDropdownOptions } from '../../models';
import { CustomAutocomplete } from '../autocomplete';
import { getInputPropsWithEndIcon } from '../utils/inputPropsHelpers';

export const SelectEditInputCell = ({
  props,
  options,
  disabledOptions = [],
  tooltipText = '',
}: {
  props: GridRenderCellParams;
  options: IDropdownOptions[];
  disabledOptions?: any[];
  tooltipText?: string;
}) => {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();

  const handleChange = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    await apiRef.current.setEditCellValue({ id, field, value: event.target.value });
    apiRef.current.stopCellEditMode({ id, field });
  };

  return (
    <TextField select sx={{ width: 160 }} value={value} onChange={handleChange} autoFocus>
      {options.map((option) => {
        const isDisabled = disabledOptions?.includes(option.id);
        return isDisabled ? (
          <Tooltip
            key={option.id}
            title={tooltipText || 'Prevailing Wage to be set for this job'}
            placement="right"
          >
            <span>
              <MenuItem value={option.id} disabled style={{ pointerEvents: 'auto' }}>
                {i18n.t(option.text)}
              </MenuItem>
            </span>
          </Tooltip>
        ) : (
          <MenuItem key={option.id} value={option.id}>
            {i18n.t(option.text)}
          </MenuItem>
        );
      })}
    </TextField>
  );
};

export const AutocompleteEditInputCell = ({
  props,
  options = [],
  placeholder,
  textfield = 'text',
  onChange,
  error = '',
  getOptionLabel,
  disabled = false,
  isOptionEqualToValue,
  multiple = false,
  limitTags = 2,
  disableClearable = false,
}: {
  placeholder?: string;
  props: GridRenderCellParams;
  options: any[];
  textfield?: string;
  onChange?: any;
  error?: string;
  getOptionLabel?: any;
  disabled?: boolean;
  isOptionEqualToValue?: any;
  multiple?: boolean;
  limitTags?: number;
  disableClearable?: boolean;
}) => {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();
  const { t } = useTranslation();
  // Normalize value for multiselect use
  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : value
      ? [value]
      : null
    : value || null;
  const handleChange = async (event: any, value: any) => {
    await apiRef.current.setEditCellValue({ id, field, value });
    apiRef.current.stopCellEditMode({ id, field });
    onChange && onChange({ apiRef, id, field, value });
  };
  return (
    <CustomAutocomplete
      id="autocomplete-selection"
      options={options}
      disabled={disabled}
      disableClearable={disableClearable}
      multiple={multiple}
      limitTags={limitTags}
      onChange={handleChange}
      value={normalizedValue}
      isOptionEqualToValue={isOptionEqualToValue ? isOptionEqualToValue : () => false}
      getOptionLabel={
        getOptionLabel
          ? getOptionLabel
          : (option: any) => (typeof option === 'string' ? option : t(option[textfield]?.trim()))
      }
      renderInput={(params: any) => (
        <TextField
          {...params}
          fullWidth
          size="medium"
          placeholder={placeholder}
          error={Boolean(error)}
          title={Boolean(error) ? error : undefined}
        />
      )}
      sx={{
        minWidth: 160,
        width: '95%',
        '.MuiOutlinedInput-root': {
          padding: '0 !important',
        },
      }}
    />
  );
};

export const MultiSelectCheckboxEditInputCell = ({
  props,
  sx,
  options = [],
  placeholder,
  textfield = 'text',
  onChange,
  error = '',
  getOptionLabel,
  disabled = false,
  isOptionEqualToValue,
  disableClearable = true,
  valueKey,
  defaultValue = [],
  disabledOption = [],
}: {
  props: GridRenderCellParams;
  sx?: Record<string, any>;
  options: any[];
  placeholder?: string;
  textfield?: string;
  onChange?: any;
  error?: string;
  getOptionLabel?: any;
  disabled?: boolean;
  isOptionEqualToValue?: any;
  disableClearable?: boolean;
  valueKey?: string;
  defaultValue?: string[];
  disabledOption?: any[];
}) => {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();
  const { t } = useTranslation();
  const prevValueRef = useRef<any[]>([]);

  useEffect(() => {
    const isEmpty = Array.isArray(value) && value.length === 0;

    if (isEmpty && Array.isArray(defaultValue) && defaultValue.length > 0) {
      apiRef.current.setEditCellValue({
        id,
        field,
        value: defaultValue,
      });

      prevValueRef.current = defaultValue;
    } else if (Array.isArray(value)) {
      prevValueRef.current = value;
    }
    // eslint-disable-next-line
  }, []);

  const normalizedValue = (() => {
    if (!valueKey) {
      return Array.isArray(value) ? value : value ? [value] : [];
    }

    if (!Array.isArray(value)) return [];
    return options.filter((opt) => value.includes(opt[valueKey]));
  })();

  const handleChange = async (_: any, newValue: any[]) => {
    let formattedValue = newValue;
    if (valueKey) {
      formattedValue = newValue.map((item) => item[valueKey]);
    }
    await apiRef.current.setEditCellValue({
      id,
      field,
      value: formattedValue,
    });
    apiRef.current.stopCellEditMode({ id, field });
    onChange &&
      onChange({ apiRef, id, field, value: formattedValue, prevValue: prevValueRef.current });
    prevValueRef.current = formattedValue;
  };
  const resolveLabel = (option: any) =>
    typeof option === 'string' ? option : t(option?.[textfield]?.trim?.() ?? '');
  return (
    <CustomAutocomplete
      multiple
      options={options}
      disabled={disabled}
      disableClearable={disableClearable}
      value={normalizedValue}
      onChange={handleChange}
      disableCloseOnSelect
      isOptionEqualToValue={
        isOptionEqualToValue
          ? isOptionEqualToValue
          : (option: any, val: any) => {
              if (!valueKey) return option === val;
              return option?.[valueKey] === val?.[valueKey];
            }
      }
      getOptionDisabled={(option: any) =>
        disabledOption?.some((opt: any) => {
          if (valueKey) {
            return opt?.[valueKey] === option?.[valueKey];
          }
          return opt === option;
        })
      }
      renderOption={(props: any, option: any, { selected }: any) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={selected} size="small" />
            {resolveLabel(option)}
          </li>
        );
      }}
      getOptionLabel={getOptionLabel ? getOptionLabel : resolveLabel}
      renderInput={(params: any) => (
        <TextField
          {...params}
          fullWidth
          size="medium"
          placeholder={placeholder}
          error={Boolean(error)}
          title={Boolean(error) ? error : undefined}
          inputProps={{ ...params.inputProps, readOnly: true }}
        />
      )}
      renderTags={(value: any[], getTagProps: any) => {
        if (!value || value.length === 0) return null;

        const first = value[0];
        const remaining = value.length - 1;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Chip {...getTagProps({ index: 0 })} label={resolveLabel(first)} size="medium" />
            {remaining > 0 && (
              <span style={{ fontSize: 12, color: 'var(--text_secondary)' }}>+{remaining}</span>
            )}
          </div>
        );
      }}
      ListboxProps={{
        sx: {
          maxHeight: 250,
          padding: 0,
          scrollbarWidth: 'none',
          '& .MuiAutocomplete-option': {
            p: '5px',
          },
        },
      }}
      sx={{
        minWidth: 160,
        maxWidth: 180,
        width: '95%',
        '.MuiOutlinedInput-root': {
          padding: '0 !important',
        },
        '.MuiChip-root': {
          fontSize: '12px',
          background: 'var(--bg_secondary)',
          color: 'var(--text_primary)',
        },
        '.MuiSvgIcon-root': {
          color: 'var(--icons_primary)',
        },
        '.Mui-disabled': {
          background: 'var(--bg_disabled) !important',
        },
        '.MuiAutocomplete-tag': {
          color: 'var(--text_primary)',
          fontSize: '12px',
        },
        ...sx,
      }}
    />
  );
};

export const TextEditInputCell = ({
  props,
  readonly = false,
  placeholder,
  disable = false,
  inputProps,
  onChange,
  type = 'text',
}: {
  props: any;
  readonly?: boolean;
  placeholder?: string;
  disable?: boolean;
  inputProps?: any;
  onChange?: any;
  type?: string;
}) => {
  const {
    id,
    value,
    field,
    row: { isNew },
    error,
    errorMessage,
  } = props;
  const apiRef = useGridApiContext();

  const handleChange = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    await apiRef.current.setEditCellValue({ id, field, value: event.target.value });
    apiRef.current.stopCellEditMode({ id, field });
    onChange && onChange({ apiRef, id, field, value: event.target.value });
  };
  return (
    <TextField
      helperText={errorMessage}
      error={Boolean(error)}
      title={error}
      placeholder={placeholder}
      disabled={(readonly && !isNew) || disable}
      sx={{ width: 160 }}
      value={value}
      type={type}
      inputProps={inputProps}
      onChange={handleChange}
    />
  );
};

export const DateTimeEditInputCell = ({
  props,
  placeholder,
  type = 'Time',
  disableOpenPicker = true,
  onChange,
  readOnly = false,
  disabled = false,
  shouldDisableDate,
  pickerclassName,
  error = '',
  isForWeek = true,
  minDate,
  defaultCalendarMonth,
  customEndAndornment,
}: {
  props: any;
  readOnly?: boolean;
  placeholder?: string;
  type?: 'Date' | 'Time';
  disableOpenPicker?: boolean;
  onChange?: any;
  disabled?: boolean;
  shouldDisableDate?: any;
  pickerclassName?: string;
  error?: string;
  isForWeek?: boolean;
  minDate?: Date;
  defaultCalendarMonth?: any;
  customEndAndornment?: React.ReactNode;
}) => {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();

  const handleChange = (newValue: string | null) => {
    apiRef.current.setEditCellValue({ id, field, value: newValue });
    apiRef.current.stopCellEditMode({ id, field });
    onChange && onChange(newValue);
  };
  const dateObj = moment(props.startDate, 'MM-DD-YYYY');
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      {type === 'Date' ? (
        <DesktopDatePicker
          inputFormat="ddd, MMM DD, YYYY"
          disableMaskedInput
          disabled={disabled}
          value={value ? moment(value) : null}
          onChange={handleChange}
          minDate={
            isForWeek ? dateObj.startOf('week').toDate().toDateString() : minDate?.toDateString()
          }
          maxDate={isForWeek ? dateObj.endOf('week').toDate().toDateString() : undefined}
          shouldDisableDate={shouldDisableDate}
          PaperProps={{ className: pickerclassName }}
          renderInput={(params: any) => (
            <TextField
              {...params}
              className="dateTimepicker"
              inputProps={{ ...params.inputProps, readOnly: true }}
              placeholder={placeholder}
              error={Boolean(error)}
            />
          )}
          defaultCalendarMonth={defaultCalendarMonth}
        />
      ) : (
        <TimePicker
          minutesStep={15}
          value={moment(value).utc()}
          ampm={false}
          disableOpenPicker={disableOpenPicker}
          disabled={disabled}
          inputFormat="HH:mm"
          onChange={handleChange}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                className="dateTimepicker"
                disabled={disabled}
                inputProps={{ ...params.inputProps, readOnly: !disableOpenPicker }}
                error={Boolean(error)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {customEndAndornment}
                      {params.InputProps.endAdornment}
                    </InputAdornment>
                  ),
                }}
              />
            );
          }}
        />
      )}
    </LocalizationProvider>
  );
};

export const CustomSwitch = (props: GridRenderCellParams) => {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();

  const handleChange = (event: React.SyntheticEvent, checked: boolean) => {
    apiRef.current.setEditCellValue({ id, field, value: checked });
  };

  return <Switch checked={Boolean(value)} onChange={handleChange} />;
};

export const CustomCheckbox = (props: any) => {
  const { id, value, field, onChange } = props;
  const apiRef = useGridApiContext();

  const handleChange = (event: React.SyntheticEvent, checked: boolean) => {
    apiRef.current.setEditCellValue({ id, field, value: checked });
    onChange && onChange(checked);
  };

  return <Checkbox checked={Boolean(value)} onChange={handleChange} />;
};

interface Props {
  props: GridRenderCellParams;
  options: IDropdownOptions[];
  label?: string; // e.g. "Select State"
  required?: boolean; // show "Required Field *"
  onChange?: (args: { id: GridRowId; field: string; value: any }) => void;
}
export const SelectEditInputCellV2 = ({
  props,
  options,
  label = '',
  required = true,
  onChange,
}: Props) => {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();

  const handleChange = async (event: ChangeEvent<{ value: unknown }>) => {
    await apiRef.current.setEditCellValue({
      id,
      field,
      value: event.target.value,
    });
    apiRef.current.stopCellEditMode({ id, field });
    onChange && onChange({ id, field, value: event.target.value });
  };

  return (
    <FormControl
      fullWidth
      size="small"
      sx={{
        m: 0,
        '& .MuiOutlinedInput-root': {
          height: 32,
          fontSize: 13,
          padding: 0,
        },
        '& .MuiSelect-select': {
          height: '32px !important',
          display: 'flex',
          alignItems: 'center',
          padding: '0 32px 0 12px',
        },
      }}
    >
      <Select
        value={value ?? ''}
        onChange={handleChange as any}
        displayEmpty
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 240, // or any height you prefer
              overflowY: 'auto',
            },
          },
        }}
      >
        {/* Placeholder option */}
        <MenuItem disabled value="" sx={{ backgroundColor: '#F5F5F5 !important' }}>
          <span style={{ color: '#626161' }}>{label}</span>
        </MenuItem>

        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {i18n.t(option.text)}
          </MenuItem>
        ))}
      </Select>

      <FormHelperText>
        Required Field{' '}
        {required && (
          <Box component="span" sx={{ color: 'error.main' }}>
            *
          </Box>
        )}
      </FormHelperText>
    </FormControl>
  );
};

export const TextEditInputCellV2 = ({
  props,
  readonly = false,
  placeholder,
  disable = false,
  onChange,
  type = 'text',
  required = true,
}: {
  props: any;
  readonly?: boolean;
  placeholder?: string;
  disable?: boolean;
  onChange?: (args: { apiRef: any; id: any; field: string; value: any }) => void;
  type?: string;
  required?: boolean;
  InputProps?: any;
}) => {
  const {
    id,
    value,
    field,
    row: { isNew },
    error,
    InputProps,
    inputProps,
  } = props;
  const apiRef = useGridApiContext();
  const handleChange = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    await apiRef.current.setEditCellValue({ id, field, value: newValue });
    apiRef.current.stopCellEditMode({ id, field });
    onChange && onChange({ apiRef, id, field, value: newValue });
  };

  return (
    <FormControl
      fullWidth
      size="small"
      sx={{
        m: 0,
        '& .MuiOutlinedInput-root': {
          height: 32,
          fontSize: 16,
          padding: 0,
        },
        '& .MuiSelect-select': {
          height: '32px !important',
          display: 'flex',
          alignItems: 'center',
          padding: '0 32px 0 12px',
        },
      }}
    >
      <TextField
        variant="outlined"
        size="small"
        error={Boolean(error)}
        title={error}
        placeholder={placeholder}
        disabled={(readonly && !isNew) || disable}
        value={value ?? ''}
        type={type}
        inputProps={inputProps}
        onChange={handleChange}
        InputProps={InputProps}
      />
      <FormHelperText sx={{ visibility: value ? 'hidden' : 'visible' }}>
        Required Field{' '}
        {required && (
          <Box component="span" sx={{ color: 'error.main' }}>
            *
          </Box>
        )}
      </FormHelperText>
    </FormControl>
  );
};

export const DateTimeEditInputCellV2 = ({
  props,
  placeholder,
  type = 'Time',
  disableOpenPicker = true,
  onChange,
  readOnly = false,
  disabled = false,
  shouldDisableDate,
  pickerclassName,
  error = '',
  isForWeek = true,
  minDate,
  helperText,
}: {
  props: any;
  readOnly?: boolean;
  placeholder?: string;
  type?: 'Date' | 'Time';
  disableOpenPicker?: boolean;
  onChange?: any;
  disabled?: boolean;
  shouldDisableDate?: any;
  pickerclassName?: string;
  error?: string | boolean;
  isForWeek?: boolean;
  minDate?: Date;
  helperText?: string;
}) => {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();

  const handleChange = (newValue: string | null) => {
    apiRef.current.setEditCellValue({ id, field, value: newValue });
    apiRef.current.stopCellEditMode({ id, field });
    onChange && onChange({ id, field, value: newValue });
  };
  console.log('date', value);
  const dateObj = moment(props.startDate, 'MM-DD-YYYY');
  const isValidDate = value && moment(value).isValid();
  const defaultHelperText = (
    <FormHelperText sx={{ visibility: isValidDate ? 'hidden' : 'visible' }}>
      Required Field <span style={{ color: 'red' }}>*</span>
    </FormHelperText>
  );

  const showError = Boolean(error);
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      {type === 'Date' ? (
        <DesktopDatePicker
          inputFormat="ddd, MMM DD, YYYY"
          disableMaskedInput
          disabled={disabled}
          value={value ? moment(value) : null}
          onChange={handleChange}
          minDate={
            isForWeek ? dateObj.startOf('week').toDate().toDateString() : minDate?.toDateString()
          }
          maxDate={isForWeek ? dateObj.endOf('week').toDate().toDateString() : undefined}
          shouldDisableDate={shouldDisableDate}
          PaperProps={{ className: pickerclassName }}
          renderInput={(params: any) => (
            <TextField
              {...params}
              size="small"
              className="dateTimepicker"
              inputProps={{ ...params.inputProps, readOnly: true }}
              placeholder={placeholder}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  padding: 0,
                  marginTop: '2px',
                },
                '& .MuiInputAdornment-root': {
                  marginRight: '8px',
                },
              }}
              error={showError}
              helperText={
                showError ? (
                  <span
                    style={{
                      display: 'block',
                      whiteSpace: 'pre-line',
                      wordBreak: 'break-word',
                      fontSize: 12,
                      lineHeight: 1,
                    }}
                  >
                    {helperText}
                  </span>
                ) : (
                  defaultHelperText
                )
              }
            />
          )}
        />
      ) : (
        <TimePicker
          minutesStep={15}
          value={moment(value).utc()}
          ampm={false}
          disableOpenPicker={disableOpenPicker}
          disabled={disabled}
          inputFormat="HH:mm"
          onChange={handleChange}
          renderInput={(params: any) => (
            <TextField
              {...params}
              className="dateTimepicker"
              disabled={disabled}
              inputProps={{ ...params.inputProps, readOnly: !disableOpenPicker }}
              error={showError}
              InputProps={getInputPropsWithEndIcon(params, <AccessTimeIcon />)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  padding: 0,
                  marginTop: '2px',
                },
                '& .MuiInputAdornment-root': {
                  marginRight: '8px',
                },
              }}
              helperText={
                showError ? (
                  <span
                    style={{
                      display: 'block',
                      whiteSpace: 'pre-line',
                      wordBreak: 'break-word',
                      fontSize: 13,
                      lineHeight: 1.4,
                    }}
                  >
                    {helperText}
                  </span>
                ) : (
                  defaultHelperText
                )
              }
            />
          )}
        />
      )}
    </LocalizationProvider>
  );
};
